import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { MODAL_DELETE_DOCUMENTS_GROUP } from '~/types/modal.types';
import { PilotId } from '~/types/PilotId';
import { SUCCESS, WARNING } from '~/types/toast.types';

type Props = {
  groupId: PilotId;
  name?: string;
};

export const DeleteGroup = ({ groupId, name = '' }: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const modal = useSelector(slices.documentGroup.selectors.selectModal);
  const { navigate } = useRouting();
  const [deleteDocumentGroup, result] =
    api.endpoints.deleteDocumentGroup.useMutation();
  const { isLoading } = result;
  const { modalType } = modal || {};

  const onClose = useCallback(() => {
    dispatch(slices.documentGroup.actions.setModal());
  }, []);

  return (
    <Modal
      isVisible={modalType === MODAL_DELETE_DOCUMENTS_GROUP}
      title={intl.formatMessage({
        id: 'DeleteGroup.title',
        defaultMessage: 'Delete Group?',
        description: 'Modal title for deleting group',
      })}
      onCancel={onClose}
      onHide={onClose}
      primaryAction={{
        text: intl.formatMessage({
          id: 'DeleteGroup.deleteButton',
          defaultMessage: 'Delete Group',
          description: 'Button text for deleting group',
        }),
        variant: 'danger',
        icon: 'trash',
        iconPosition: 'left',
        isLoading,
        onClick: () => {
          deleteDocumentGroup(groupId)
            .unwrap()
            .then(() => {
              showToast(
                SUCCESS,
                intl.formatMessage({
                  id: 'DeleteGroup.deleteSuccess',
                  defaultMessage: 'Group has been deleted successfully.',
                  description: 'Success message when group is deleted',
                }),
              );
              onClose();
              navigate('/document-groups');
            })
            .catch(() => {
              showToast(
                WARNING,
                intl.formatMessage({
                  id: 'DeleteGroup.deleteError',
                  defaultMessage:
                    'An error occurred while deleting the document group.',
                  description: 'Error message when group deletion fails',
                }),
              );
            });
        },
      }}
    >
      <Text>
        <FormattedMessage
          id="DeleteGroup.confirmationMessage"
          defaultMessage="Are you sure you want to delete the group {groupName}? You won't be able to undo this action."
          description="Confirmation message for group deletion. Example: groupName = 'Contract Group A'"
          values={{ groupName: name }}
        />
      </Text>
    </Modal>
  );
};
